import { cn, formatRelativeTime } from '@/lib/utils';
import type { Ticket } from '@/lib/api';
import { TicketListSkeleton } from '@/components/Skeleton';
import { Avatar } from '@/components/Avatar';
import { StatusBadge } from '@/components/StatusBadge';
import { CoreIcon } from '@/components/CoreIcon';

function senderName(email: string): string {
  const local = email.includes('@') ? email.split('@')[0] : email;
  return local
    .split(/[._\-+]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ') || email;
}

export function TicketList(props: {
  tickets: Ticket[];
  selectedId: string | null;
  loading: boolean;
  onSelect: (t: Ticket) => void;
}) {
  if (props.loading && props.tickets.length === 0) {
    return <TicketListSkeleton />;
  }
  if (props.tickets.length === 0) {
    return (
      <div role="status" className="p-6 text-sm text-muted-foreground">
        No tickets match this filter yet.
      </div>
    );
  }
  return (
    <ul
      className="overflow-y-auto flex-1 min-h-0"
      aria-label="Support tickets"
    >
      {props.tickets.map((t) => {
        const selected = props.selectedId === t.id;
        const needsReview = t.status === 'awaiting_approval';
        const name = senderName(t.from_email);
        return (
          <li key={t.id} className="border-b border-border/70 last:border-b-0">
            <button
              type="button"
              aria-current={selected ? 'true' : undefined}
              aria-label={`Ticket from ${t.from_email}: ${t.subject ?? 'no subject'}`}
              className={cn(
                'w-full text-left p-3.5 flex gap-3 transition-colors',
                'hover:bg-muted/60',
                selected && 'bg-muted relative',
              )}
              onClick={() => props.onSelect(t)}
            >
              {selected && (
                <span
                  aria-hidden
                  className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent"
                />
              )}
              <div className="relative">
                <Avatar seed={t.from_email} label={name} size={40} />
                <StatusBadge
                  status={t.status}
                  dotOnly
                  className="absolute -right-0.5 -bottom-0.5 ring-2 ring-surface"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-foreground truncate">
                    {name}
                  </span>
                  <span className="text-[11px] text-muted-foreground shrink-0 tabular-nums">
                    {formatRelativeTime(t.last_activity)}
                  </span>
                </div>
                <div className="text-[13px] text-foreground/90 truncate mt-0.5">
                  {t.subject ?? '(no subject)'}
                </div>
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  {needsReview && <StatusBadge status="awaiting_approval" />}
                  {t.ai_category && t.ai_category !== 'other' && (
                    <span className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-1.5 py-0.5 text-[10.5px] font-medium uppercase tracking-mono text-muted-foreground">
                      {t.ai_category}
                    </span>
                  )}
                  {typeof t.ai_confidence === 'number' && (
                    <span className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground">
                      <span className="text-accent"><CoreIcon name="sparkles" size={10} /></span>
                      {Math.round(t.ai_confidence * 100)}%
                    </span>
                  )}
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
